import React, { useState, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import {
  Upload, FileKey, Loader2, Eye, EyeOff, Sparkles, CheckCircle2, AlertCircle, ShieldCheck, Download,
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const COUNTRY_INFO = {
  EC: { name: 'Ecuador', norm: 'Ley de Comercio Electronico · Certificados BCE' },
  CO: { name: 'Colombia', norm: 'Ley 527 de 1999 · Firma digital regulada' },
  PE: { name: 'Peru', norm: 'Ley de Firmas Digitales · RENIEC' },
  CL: { name: 'Chile', norm: 'Ley 19.799 · Firma electronica avanzada' },
  MX: { name: 'Mexico', norm: 'e.firma SAT' },
  AR: { name: 'Argentina', norm: 'Ley 25.506 · PKI estatal' },
};

/**
 * Panel reutilizable para cargar un .p12 + password y devolver al padre los bytes.
 *
 * Props:
 *  - onReady(p12Base64, password, certInfo) — cuando se valida correctamente el .p12
 *  - onClear() — cuando se reinicia
 *  - busy — boolean externo para deshabilitar UI
 *  - authToken — para llamar al backend inspect-p12 y generate-test-p12 (opcional, si null
 *    se asume contexto publico y se usa endpoint /signatures/inspect-p12-public).
 *  - defaultCountry — pais por defecto en el wizard de certificado de prueba
 */
const CertSignaturePanel = ({ onReady, onClear, busy = false, authToken = null, defaultCountry = 'EC' }) => {
  const fileRef = useRef(null);
  const [p12Base64, setP12Base64] = useState(null);
  const [filename, setFilename] = useState('');
  const [password, setPassword] = useState('');
  const [showPwd, setShowPwd] = useState(false);
  const [validating, setValidating] = useState(false);
  const [certInfo, setCertInfo] = useState(null);
  const [error, setError] = useState(null);

  // Test cert generator
  const [showGen, setShowGen] = useState(false);
  const [genForm, setGenForm] = useState({
    common_name: '',
    email: '',
    password: '',
    country: defaultCountry,
  });
  const [generating, setGenerating] = useState(false);

  const authHeaders = () => authToken ? { Authorization: `Bearer ${authToken}` } : {};

  const handleFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 1024 * 1024) { toast.error('Archivo demasiado grande (max 1MB)'); return; }
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result;
      const b64 = result.includes(',') ? result.split(',', 2)[1] : result;
      setP12Base64(b64);
      setFilename(f.name);
      setCertInfo(null);
      setError(null);
    };
    reader.readAsDataURL(f);
  };

  const validate = async () => {
    if (!p12Base64) { toast.error('Selecciona el archivo .p12'); return; }
    if (!password) { toast.error('Ingresa la contrasena del certificado'); return; }
    setValidating(true); setError(null); setCertInfo(null);
    try {
      const r = await axios.post(`${API}/signatures/inspect-p12`,
        { p12_base64: p12Base64, password },
        { headers: authHeaders() });
      if (r.data.valid) {
        setCertInfo(r.data);
        onReady?.(p12Base64, password, r.data);
        toast.success('Certificado validado');
      } else {
        setError(r.data.error || 'Certificado invalido');
        toast.error(r.data.error || 'Certificado invalido o contrasena incorrecta');
      }
    } catch (e) {
      const msg = e?.response?.data?.detail || 'Error al validar';
      setError(msg);
      toast.error(msg);
    } finally { setValidating(false); }
  };

  const clear = () => {
    setP12Base64(null); setFilename(''); setPassword(''); setCertInfo(null); setError(null);
    if (fileRef.current) fileRef.current.value = '';
    onClear?.();
  };

  const generateTestCert = async () => {
    if (!genForm.common_name || !genForm.email || !genForm.password) {
      toast.error('Nombre, email y contrasena requeridos'); return;
    }
    setGenerating(true);
    try {
      const r = await axios.post(`${API}/signatures/generate-test-p12`,
        { ...genForm, days_valid: 365 },
        { headers: authHeaders() });
      // Auto-cargar el .p12 generado
      setP12Base64(r.data.p12_base64);
      setFilename(r.data.filename);
      setPassword(genForm.password);
      setCertInfo(r.data.info);
      onReady?.(r.data.p12_base64, genForm.password, r.data.info);
      toast.success('Certificado de prueba generado y cargado');
      setShowGen(false);
      // Ofrecer descarga
      const blob = new Blob([Uint8Array.from(atob(r.data.p12_base64), c => c.charCodeAt(0))], { type: 'application/x-pkcs12' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = r.data.filename;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Error al generar certificado');
    } finally { setGenerating(false); }
  };

  return (
    <div className="space-y-3" data-testid="cert-signature-panel">
      {/* Step 1: cargar .p12 */}
      <div>
        <Label className="flex items-center gap-1 text-sm font-semibold text-[#2F3D8E]">
          <FileKey className="w-4 h-4" /> Certificado digital (.p12 / .pfx)
        </Label>
        <p className="text-xs text-gray-500 mb-2">Formatos PKCS#12 (Ecuador BCE, Colombia, Peru RENIEC, Chile, Mexico SAT, Argentina).</p>
        {!p12Base64 ? (
          <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 text-center cursor-pointer hover:border-[#2F3D8E]" onClick={() => fileRef.current?.click()}>
            <Upload className="w-8 h-8 text-gray-400 mx-auto mb-2" />
            <p className="text-sm text-gray-600">Haga clic para seleccionar su .p12</p>
            <input ref={fileRef} type="file" accept=".p12,.pfx" onChange={handleFile} className="hidden" data-testid="cert-file-input" />
          </div>
        ) : (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 flex items-center justify-between">
            <div className="flex items-center gap-2 text-sm">
              <FileKey className="w-5 h-5 text-[#2F3D8E]" />
              <span className="font-medium text-[#1a2456] truncate">{filename}</span>
            </div>
            <button onClick={clear} className="text-xs text-red-500 hover:underline">Quitar</button>
          </div>
        )}
      </div>

      {/* Step 2: password */}
      {p12Base64 && !certInfo && (
        <div>
          <Label className="text-sm">Contrasena del certificado</Label>
          <div className="flex gap-2 mt-1">
            <div className="relative flex-1">
              <Input type={showPwd ? 'text' : 'password'} value={password} onChange={e => setPassword(e.target.value)}
                     placeholder="Ingrese contrasena" data-testid="cert-password-input" autoComplete="off"
                     onKeyDown={e => { if (e.key === 'Enter') validate(); }} />
              <button onClick={() => setShowPwd(s => !s)} className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400">
                {showPwd ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
            <Button onClick={validate} disabled={validating || !password} className="bg-[#2F3D8E]" data-testid="validate-cert-btn">
              {validating ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Validar'}
            </Button>
          </div>
          {error && (
            <p className="text-xs text-red-600 mt-1 flex items-center gap-1">
              <AlertCircle className="w-3 h-3" /> {error}
            </p>
          )}
        </div>
      )}

      {/* Step 3: cert info */}
      {certInfo && certInfo.valid && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-3 text-xs space-y-1" data-testid="cert-info">
          <div className="flex items-center gap-2 font-semibold text-green-700 mb-1">
            <CheckCircle2 className="w-4 h-4" /> Certificado validado
          </div>
          <div><strong>Titular:</strong> {certInfo.common_name}</div>
          <div><strong>Email:</strong> {certInfo.email || '-'}</div>
          <div><strong>Organizacion:</strong> {certInfo.organization || '-'}</div>
          <div><strong>Pais:</strong> {certInfo.country || '-'} {COUNTRY_INFO[certInfo.country] ? `· ${COUNTRY_INFO[certInfo.country].norm}` : ''}</div>
          <div><strong>Emisor:</strong> {certInfo.issuer?.commonName || '-'}</div>
          <div><strong>Valido hasta:</strong> {new Date(certInfo.not_valid_after).toLocaleDateString('es-EC')}</div>
          {certInfo.self_signed && (
            <div className="mt-2 px-2 py-1 bg-amber-100 text-amber-800 rounded text-[10px] flex items-center gap-1">
              <AlertCircle className="w-3 h-3" /> Certificado auto-firmado (solo prueba, no validez legal en produccion).
            </div>
          )}
        </div>
      )}

      {/* Generate test cert helper */}
      {!certInfo && (
        <div className="border-t pt-2">
          {!showGen ? (
            <button onClick={() => {
              setShowGen(true);
              setGenForm(g => ({ ...g, common_name: g.common_name }));
            }} className="text-xs text-[#2F3D8E] hover:underline flex items-center gap-1" data-testid="open-gen-test-cert">
              <Sparkles className="w-3 h-3" /> No tengo un .p12 — generar uno de prueba (self-signed)
            </button>
          ) : (
            <div className="bg-gray-50 border rounded-lg p-3 space-y-2">
              <p className="text-xs text-gray-600 font-semibold">Generar certificado de prueba (self-signed)</p>
              <div className="grid grid-cols-2 gap-2">
                <Input placeholder="Nombre completo" className="h-8 text-xs" value={genForm.common_name} onChange={e => setGenForm({ ...genForm, common_name: e.target.value })} data-testid="gen-cn" />
                <Input placeholder="Email" className="h-8 text-xs" value={genForm.email} onChange={e => setGenForm({ ...genForm, email: e.target.value })} data-testid="gen-email" />
                <Input placeholder="Contrasena .p12" type="text" className="h-8 text-xs" value={genForm.password} onChange={e => setGenForm({ ...genForm, password: e.target.value })} data-testid="gen-pwd" />
                <select className="h-8 text-xs border rounded px-2" value={genForm.country} onChange={e => setGenForm({ ...genForm, country: e.target.value })}>
                  {Object.entries(COUNTRY_INFO).map(([c, info]) => (
                    <option key={c} value={c}>{info.name}</option>
                  ))}
                </select>
              </div>
              <div className="flex gap-2">
                <Button size="sm" variant="ghost" onClick={() => setShowGen(false)}>Cancelar</Button>
                <Button size="sm" onClick={generateTestCert} disabled={generating} className="bg-[#4C8C3B] hover:bg-[#3d7030]" data-testid="generate-test-cert-btn">
                  {generating ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Download className="w-3 h-3 mr-1" />}
                  Generar y descargar
                </Button>
              </div>
            </div>
          )}
        </div>
      )}

      <div className="bg-gray-50 rounded-lg p-2 text-[10px] text-gray-500 flex items-start gap-1.5">
        <ShieldCheck className="w-3 h-3 mt-0.5 flex-shrink-0 text-[#2F3D8E]" />
        <span>El archivo .p12 NO se guarda en el servidor. Se usa unicamente para firmar criptograficamente el PDF en memoria. Compatible con normativas LATAM (EC/CO/PE/CL/MX/AR).</span>
      </div>
    </div>
  );
};

export default CertSignaturePanel;
